import { useState, useEffect, useMemo } from "react";
import { FaSearch, FaShieldAlt, FaUser, FaCalendar, FaCode, FaSignal } from "react-icons/fa";

import Pagination from "Components/Pagination";
import { getPossibilityHistory } from "JS/API";
import { useMarkMenuSeen } from "Contexts/NewCountContext";
import { commonModalSetting, enterFn } from "JS/common";
import CommonModal from "../Components/CommonModal";

// 디자인 시스템 임포트
import PageContainer from "Components/UI/PageContainer";
import SearchCard from "Components/UI/SearchCard";
import FormField from "Components/UI/FormField";
import Input from "Components/UI/Input";
import Select from "Components/UI/Select";
import Button from "Components/UI/Button";
import Badge from "Components/UI/Badge";
import { TableContainer, Table, TableHeader, TableCell } from "Components/UI/Table";
import { commonStyles, designTokens, listPageStyles } from "utils/designSystem";

const tableHeaders = [
  {
    key: "id",
    label: "No.",
  },
  {
    key: "userId",
    label: "사용자이름(아이디)",
  },
  {
    key: "keyword",
    label: "키워드",
  },
  {
    key: "code",
    label: "류/유사군코드",
  },
  {
    key: "similarity",
    label: "유사도",
  },
  {
    key: "similarityStrength",
    label: "유사도 강도",
  },
  {
    key: "createdAt",
    label: "생성일",
  },
];

const useRowSelection = (data) => {
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const isDataEmpty = data.length === 0;
  const allRowIds = useMemo(() => new Set(data.map((item) => item.id)), [data]);

  const isAllSelected = useMemo(() => {
    if (isDataEmpty) {
      return false;
    }
    return selectedRowIds.size === allRowIds.size;
  }, [selectedRowIds, allRowIds, isDataEmpty]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRowIds(new Set(allRowIds));
    } else {
      setSelectedRowIds(new Set());
    }
  };

  const handleRowSelect = (rowId) => {
    const newSelectedRowIds = new Set(selectedRowIds);
    if (newSelectedRowIds.has(rowId)) {
      newSelectedRowIds.delete(rowId);
    } else {
      newSelectedRowIds.add(rowId);
    }
    setSelectedRowIds(newSelectedRowIds);
  };

  const clearSelectedRows = () => {
    setSelectedRowIds(new Set());
  };

  return {
    selectedRowIds,
    handleRowSelect,
    handleSelectAll,
    clearSelectedRows,
    isAllSelected,
    isDataEmpty,
  };
};

const PossibilityInfringementTable = ({
  data,
  selectedRowIds,
  onRowSelect,
  onSelectAll,
  isAllSelected,
  isDataEmpty,
}) => {
  // 추가 스타일
  const tableStyles = {
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs,
      fontWeight: '500'
    },
    userDetail: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    codeInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs
    },
    probabilityInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs,
      fontWeight: '600'
    },
    levelInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs
    },
    dateInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.xs
    },
    checkbox: {
      transform: 'scale(1.3)',
      cursor: 'pointer'
    },
    checkboxLabel: listPageStyles.checkboxLabel};

  // 유사도 강도별 배지 variant
  const getLevelBadgeVariant = (level) => {
    const variants = {
      '높음': 'error',
      '보통': 'warning',
      '낮음': 'success'
    };
    return variants[level] || 'neutral';
  };

  // 유사도 색상
  const getProbabilityColor = (probability) => {
    const prob = parseFloat(probability);
    if (prob >= 80) return designTokens.colors.error;
    if (prob >= 50) return designTokens.colors.warning;
    return designTokens.colors.success;
  };

  return (
    <TableContainer>
      <Table>
        <colgroup>
          <col width="10%" />
          <col width="15%" />
          <col width="20%" />
          <col width="20%" />
          <col width="10%" />
          <col width="10%" />
          <col width="15%" />
        </colgroup>
        <thead>
          <tr>
            {tableHeaders.map((header) => (
              <TableHeader key={header.key}>
                {header.key === "select" ? (
                  <label style={tableStyles.checkboxLabel}>
                    <input
                      type="checkbox"
                      style={tableStyles.checkbox}
                      onChange={onSelectAll}
                      checked={isAllSelected}
                      disabled={isDataEmpty}
                      aria-label="모든 행 선택 또는 선택 해제"
                    />
                  </label>
                ) : (
                  header.label
                )}
              </TableHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((row) => (
            <tr key={row.id}>
              <TableCell style={{ fontWeight: '600' }}>
                {row.id}
              </TableCell>

              <TableCell>
                <div style={tableStyles.userDetail}>
                  <div style={tableStyles.userInfo}>
                    <FaUser size={12} style={{ color: designTokens.colors.text.tertiary }} />
                    {row.user_name}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: designTokens.colors.text.secondary,
                    marginLeft: '16px'
                  }}>
                    ({row.user_id})
                  </div>
                </div>
              </TableCell>

              <TableCell>
                <span style={{
                  fontWeight: '500',
                  wordBreak: 'break-all',
                  lineHeight: '1.4'
                }}>
                  {row.input_text}
                </span>
              </TableCell>

              <TableCell>
                <div style={tableStyles.codeInfo}>
                  <FaCode size={12} style={{ color: designTokens.colors.text.tertiary }} />
                  <span>
                    {row.input_maincode && row.input_maincode + "류/"}
                    {row.input_subcode}
                  </span>
                </div>
              </TableCell>

              <TableCell>
                <div style={{
                  ...tableStyles.probabilityInfo,
                  color: getProbabilityColor(row.result_probability)
                }}>
                  {row.result_probability}%
                </div>
              </TableCell>

              <TableCell>
                <Badge variant={getLevelBadgeVariant(row.input_level)}>
                  <FaSignal size={10} style={{ marginRight: '4px' }} />
                  {row.input_level}
                </Badge>
              </TableCell>

              <TableCell>
                <div style={tableStyles.dateInfo}>
                  <FaCalendar size={12} style={{ color: designTokens.colors.text.tertiary }} />
                  {row.created_at.split("T")[0]}
                </div>
              </TableCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

const SearchComponent = ({ onSearch }) => {
  const [select, setSelect] = useState("all");
  const [query, setQuery] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  // 추가 스타일
  const searchStyles = {
    searchSection: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: designTokens.spacing.lg,
      flexWrap: 'wrap'
    },
    searchGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      flex: 1,
      minWidth: '300px'
    }
  };

  const handleChangeSelect = (e) => {
    setSelect(e.target.value);
  };

  const handleChangeInput = (e) => {
    setQuery(e.target.value);
  };

  const handleClickBtn = () => {
    if (query.trim() === "") {
      commonModalSetting(setAlertBox, true, "alert", "검색어를 입력해 주세요.");
      return;
    }
    onSearch(select, query);
  };

  const handleSearchReset = () => {
    setQuery("");
    setSelect("all");
    onSearch("all", "");
  };

  return (
    <>
      <SearchCard>
        <div style={searchStyles.searchSection}>
          <div style={searchStyles.searchGroup}>
            <FormField style={{ minWidth: '160px', marginBottom: 0 }}>
              <Select value={select} onChange={handleChangeSelect}>
                <option value="all">전체 보기</option>
                <option value="user_id">아이디</option>
                <option value="keyword">키워드</option>
              </Select>
            </FormField>

            <FormField style={{ flex: 1, marginBottom: 0 }}>
              <Input
                type="text"
                placeholder="검색어를 입력하세요."
                value={query}
                onChange={handleChangeInput}
                onKeyDown={(e) => {
                  if (!alertBox.bool) enterFn(e, handleClickBtn);
                }}
              />
            </FormField>

            <Button
              variant="primary"
              icon={<FaSearch />}
              onClick={handleClickBtn}
            >
              검색
            </Button>

            {(query || select !== "all") && (
              <Button
                variant="secondary"
                onClick={handleSearchReset}
              >
                초기화
              </Button>
            )}
          </div>
        </div>
      </SearchCard>

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => setAlertBox({ mode: "", context: "", bool: false })}
        />
      )}
    </>
  );
};

const PossibilityInfringement = () => {
  useMarkMenuSeen("possibility");
  const [tapHistory, setTapHistory] = useState([]);
  const {
    selectedRowIds,
    handleRowSelect,
    handleSelectAll,
    clearSelectedRows,
    isAllSelected,
    isDataEmpty,
  } = useRowSelection(tapHistory);
  const [pageInfo, setPageInfo] = useState({
    page: 1,
    totalPage: 1,
    limit: 10,
  });

  const [searchParams, setSearchParams] = useState({
    searchType: null,
    searchValue: null,
  });

  // 추가 스타일
  const pageStyles = {
    noDataContainer: listPageStyles.noDataContainer,
    controlGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
      marginBottom: designTokens.spacing.lg
    }
  };

  const handleSearch = (searchType, searchValue) => {
    setSearchParams({ searchType, searchValue });
    setPageInfo((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 침해 가능성 검사";
  }, []);

  useEffect(() => {
    const fetchTapHistory = async () => {
      try {
        const { page, limit } = pageInfo;
        const { searchType, searchValue } = searchParams;

        const response = await getPossibilityHistory(
          page,
          limit,
          searchType,
          searchValue
        );
        const totalPage = response?.data?.meta?.total_page + 1 ?? 0;

        setPageInfo((prev) => ({ ...prev, totalPage }));
        setTapHistory(response.data.data);
        clearSelectedRows();
      } catch (error) {
        console.error("데이터 불러오기 오류:", error);
      }
    };

    fetchTapHistory();
  }, [pageInfo.page, pageInfo.limit, searchParams]);

  return (
    <PageContainer
      title="POSSIBILITY INFRINGEMENT"
      icon={<FaShieldAlt />}
    >
      {/* 검색 컴포넌트 */}
      <SearchComponent onSearch={handleSearch} />

      {/* 페이지 설정 */}
      <div style={pageStyles.controlGroup}>
        <Select
          value={pageInfo.limit}
          onChange={(e) => {
            setPageInfo((prev) => ({
              ...prev,
              page: 1,
              limit: parseInt(e.target.value)
            }));
          }}
          style={{ minWidth: '140px' }}
        >
          <option value={10}>10개씩 보기</option>
          <option value={30}>30개씩 보기</option>
          <option value={50}>50개씩 보기</option>
        </Select>
      </div>

      {/* 선택된 항목 정보 */}
      {selectedRowIds.size > 0 && (
        <div style={{
          ...commonStyles.card,
          backgroundColor: designTokens.colors.primary + '10',
          border: `1px solid ${designTokens.colors.primary}30`,
          marginBottom: designTokens.spacing.lg,
          padding: designTokens.spacing.md
        }}>
          <span style={{ fontWeight: '500', color: designTokens.colors.primary }}>
            {selectedRowIds.size}개 항목이 선택되었습니다.
          </span>
        </div>
      )}

      {/* 테이블 또는 빈 상태 */}
      {tapHistory?.length ? (
        <PossibilityInfringementTable
          data={tapHistory}
          selectedRowIds={selectedRowIds}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          isDataEmpty={isDataEmpty}
        />
      ) : (
        <div style={pageStyles.noDataContainer}>
          <FaShieldAlt
            size={48}
            style={{
              color: designTokens.colors.text.muted,
              marginBottom: designTokens.spacing.md
            }}
          />
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: designTokens.spacing.xs }}>
            {searchParams.searchValue ? '검색 결과가 없습니다' : '침해 가능성 검사 결과가 없습니다'}
          </div>
          <div style={{ fontSize: '14px' }}>
            {searchParams.searchValue
              ? '다른 검색어로 시도해보세요.'
              : '검사 결과가 표시될 예정입니다.'
            }
          </div>
        </div>
      )}

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />
    </PageContainer>
  );
};

export default PossibilityInfringement;